/**
 * useTouchMode — ¿este dispositivo se maneja con el dedo?
 *
 * POR QUÉ EXISTE
 * ---------------------------------------------------------------------------
 * El visor de rack y el mapa de planta se manejan bien con ratón, pero en una
 * tablet delante del armario hacen falta otras cosas: botones grandes, una cara
 * del rack cada vez (no caben tres columnas), hojas deslizantes en vez de
 * paneles laterales y **tocar para colocar** en lugar de arrastrar (el
 * drag-and-drop de HTML5 no dispara eventos en pantalla táctil).
 *
 * `(pointer: coarse)` es la señal correcta: la consulta responde por el
 * dispositivo de entrada PRINCIPAL, así que un portátil con pantalla táctil y
 * ratón sigue en modo escritorio. `(hover: none)` cubre los navegadores que no
 * implementan `pointer` pero sí saben que no hay hover.
 *
 * Se puede forzar el modo desde la propia UI (botón 📱/🖥 del visor de rack):
 * la preferencia se guarda en localStorage y vive fuera de este hook.
 *
 * En Jest/jsdom no existe `matchMedia`, así que devuelve `false` y los tests
 * siguen viendo el modo escritorio de siempre.
 */

import { useEffect, useState } from 'react';

export const TOUCH_QUERY = '(pointer: coarse), (hover: none)';

function matches(query) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia(query).matches;
  } catch {
    return false;
  }
}

export default function useTouchMode(query = TOUCH_QUERY) {
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const [coarse, setCoarse] = useState(() => matches(query));

  useEffect(() => {
    if (!supported) return undefined;
    let mql;
    try {
      mql = window.matchMedia(query);
    } catch {
      return undefined;
    }
    const onChange = (event) => setCoarse(event.matches);
    setCoarse(mql.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [query, supported]);

  return coarse;
}
